import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { waybillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

export default function TruckingWaybillPrintPage() {
  const { printToken } = useParams();
  const [waybill, setWaybill] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [storeInfo, setStoreInfo] = useState({});

  useEffect(() => {
    if (!printToken) return;
    waybillAPI.getByPrintToken(printToken)
      .then((res) => {
        const d = res.data;
        if (d.success) {
          setWaybill(d.data.waybill);
          setLines(Array.isArray(d.data.lines) ? d.data.lines : []);
          setStoreInfo(d.data.storeInfo || {});
        } else {
          setError(d.message || 'Failed to load waybill.');
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to load waybill.');
      })
      .finally(() => setLoading(false));
  }, [printToken]);

  useEffect(() => {
    if (!waybill || loading) return;
    const prev = document.title;
    document.title = `TruckingWaybill-${waybill?.SerialNo || ''}`;
    const t = setTimeout(() => window.print(), 400);
    return () => { clearTimeout(t); document.title = prev; };
  }, [waybill, loading]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading…</div>;
  if (error)   return <div className="p-8 text-center text-red-500">{error}</div>;

  const safeWaybill = waybill || {};
  const safeLines   = Array.isArray(lines) ? lines : [];

  if (!safeWaybill._id) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        No waybill data to print.
      </div>
    );
  }

  // Compute totals — trucking has no minimum floor
  const totalAmount = safeLines.reduce((s, l) => s + (l.SubTotal || 0), 0);
  const netOfVAT   = totalAmount / 1.12;
  const vat        = netOfVAT * 0.12;
  const withholding = netOfVAT * 0.02;
  const stripFee     = parseFloat(safeWaybill.StrippingFee) || 0;
  const addlStripFee = parseFloat(safeWaybill.AdditionalStrippingFee) || 0;
  const amountDue  = safeWaybill.HasWithHolding
    ? totalAmount + stripFee + addlStripFee - withholding
    : totalAmount + stripFee + addlStripFee;
  const totalQty = safeLines.reduce((s, l) => s + (l.Quantity || 0), 0);

  const customerName =
    safeWaybill.CustomerId?.CustomerName ||
    safeWaybill.CustomerId?.name         ||
    safeWaybill.CustomerName             ||
    safeWaybill.customerName             || '';

  const isVoided = !!(safeWaybill.isVoided || safeWaybill.IsVoided);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', padding: '24px', maxWidth: '900px', margin: '0 auto', background: '#fff' }}>
      <style>{`
        @media print {
          body { margin: 0; }
          @page { size: A4; margin: 15mm; }
        }
        table.waybill-table { width: 100%; border-collapse: collapse; }
        table.waybill-table td, table.waybill-table th {
          border: 1px solid #ccc; padding: 4px 6px; font-size: 11pt;
        }
        .no-padding { margin: 4px 0; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .row { display: flex; gap: 0; }
        .col-half { flex: 1; }
        .void-watermark {
          position: fixed;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 96pt; font-weight: 900;
          color: rgba(220, 38, 38, 0.18);
          pointer-events: none; z-index: 1000;
          user-select: none; white-space: nowrap; letter-spacing: 8px;
        }
        @media print { .void-watermark { position: fixed; } }
        .sig-row { display: flex; justify-content: space-between; margin-top: 32px; }
        .sig-col { flex: 1; text-align: left; padding-right: 16px; }
      `}</style>

      {isVoided && <div className="void-watermark">VOID</div>}

      {/* Company Header */}
      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
        <h2 style={{ margin: '0 0 2px', fontSize: '14pt' }}>{storeInfo.companyName || 'ARVANNAH RENZ TRUCKING INC.'}</h2>
        <p style={{ margin: '0 0 2px', fontSize: '10pt' }}>
          {storeInfo.address || 'Q. Abeto St., Brgy. Abeto-Mirasol Taft South Manduriao, Iloilo City'}
        </p>
        <p style={{ margin: '0 0 8px', fontWeight: 'bold', fontSize: '12pt' }}>TRANSPORT WAYBILL</p>
      </div>

      {isVoided && (
        <div style={{
          background: '#fef2f2', border: '2px solid #dc2626',
          borderRadius: 6, padding: '8px 16px', marginBottom: 12,
          textAlign: 'center', color: '#dc2626', fontWeight: 700, fontSize: '11pt',
        }}>
          ⊘ VOIDED
          {safeWaybill.voidReason && ` — ${safeWaybill.voidReason}`}
          {safeWaybill.voidedAt && ` on ${new Date(safeWaybill.voidedAt).toLocaleDateString('en-PH')}`}
        </div>
      )}

      {/* Info block */}
      <div className="row" style={{ marginBottom: '8px' }}>
        <div className="col-half">
          <p className="no-padding"><b>Serial No:</b> {safeWaybill.SerialNo}</p>
          {safeWaybill.referenceNumber && (
            <p className="no-padding"><b>Ref No.:</b> {safeWaybill.referenceNumber}</p>
          )}
          <p className="no-padding"><b>Shipper:</b> {safeWaybill.Shipper}</p>
          <p className="no-padding"><b>Customer:</b> {customerName}</p>
          <p className="no-padding"><b>Origin:</b> {safeWaybill.Origin}</p>
          <p className="no-padding"><b>Vessel:</b> {safeWaybill.Vessel}</p>
          <p className="no-padding"><b>Plate No.:</b> {safeWaybill.PlateNo}</p>
        </div>
        <div className="col-half">
          <p className="no-padding"><b>Date:</b> {fmt(safeWaybill.TransactionDate)}</p>
          <p className="no-padding"><b>Shipper Address:</b> {safeWaybill.ShipperAddress}</p>
          <p className="no-padding"><b>Customer Address:</b> {safeWaybill.CustomerAddress}</p>
          <p className="no-padding"><b>Destination:</b> {safeWaybill.Destination}</p>
          <p className="no-padding"><b>Voyage:</b> {safeWaybill.Voyage}</p>
          <p className="no-padding"><b>Van No:</b> {safeWaybill.VanNo}</p>
          {stripFee > 0 && (
            <p className="no-padding"><b>Stripping Fee:</b> ₱{PHP(stripFee)}</p>
          )}
          {addlStripFee > 0 && (
            <p className="no-padding"><b>Add'l Stripping Fee:</b> ₱{PHP(addlStripFee)}</p>
          )}
        </div>
      </div>

      {(safeWaybill.Description || safeWaybill.notes) && (
        <div style={{ marginBottom: '8px' }}>
          <b>Remarks:</b>
          <p style={{ margin: '2px 0 0' }}>{safeWaybill.Description || safeWaybill.notes}</p>
        </div>
      )}

      {/* Line items table */}
      <table className="waybill-table" style={{ marginBottom: '0' }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th>Description</th>
            <th>UOM</th>
            <th className="text-right">Quantity</th>
            <th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {safeLines.map((l) => (
            <tr key={l._id}>
              <td>{l.Description}</td>
              <td>{l.UnitOfMeasureId?.Abbreviation || l.UnitOfMeasureId?.UnitOfMeasureName || ''}</td>
              <td className="text-right">{l.Quantity}</td>
              <td className="text-right">{PHP(l.SubTotal)}</td>
            </tr>
          ))}

          {/* Total row */}
          <tr style={{ fontWeight: 'bold' }}>
            <td colSpan={2}><b>Total Amount</b></td>
            <td className="text-right"><b>{PHP(totalQty)}</b></td>
            <td className="text-right"><b>{PHP(totalAmount)}</b></td>
          </tr>

          {/* Summary rows */}
          <tr>
            <td style={{ verticalAlign: 'top', lineHeight: '1.8' }}>
              <div>NET of VAT:</div>
              <div>VAT:</div>
              {stripFee > 0 && <div>Stripping Fee:</div>}
              {addlStripFee > 0 && <div>Add'l Stripping Fee:</div>}
              {safeWaybill.HasWithHolding && <div>Less: Withholding:</div>}
              <div><b>Amount Due:</b></div>
            </td>
            <td colSpan={3} className="text-right" style={{ verticalAlign: 'top', lineHeight: '1.8' }}>
              <div>{PHP(netOfVAT)}</div>
              <div>{PHP(vat)}</div>
              {stripFee > 0 && <div>{PHP(stripFee)}</div>}
              {addlStripFee > 0 && <div>{PHP(addlStripFee)}</div>}
              {safeWaybill.HasWithHolding && <div>{PHP(withholding)}</div>}
              <div><b>{PHP(amountDue)}</b></div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Signatures */}
      <div className="sig-row">
        <div className="sig-col">
          <p>Dispatched By:</p>
          <p style={{ marginTop: '24px' }}>___________________</p>
        </div>
        <div className="sig-col">
          <p>Delivered by:</p>
          <p style={{ marginTop: '24px' }}>___________________</p>
        </div>
        <div className="sig-col">
          <p>Received goods in good order/condition:</p>
          <p style={{ marginTop: '24px' }}>___________________</p>
        </div>
      </div>

      <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '10pt', color: '#555' }}>
        {new Date().toLocaleString()}
      </p>
    </div>
  );
}
